"use client";

import {
  useMemo,
  useState,
  useEffect,
  useRef,
  type HTMLAttributes,
  type FC,
  type Ref,
} from "react";
import {
  Image,
  useTheme,
  useRotationParallax,
  type ClassNameProp,
  useParentHover,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { IllustrationName } from "../../types/IllustrationType";
import { illustrations } from "../../config/illustrations";
import { IllustrationHead } from "../IllustrationHead";

import {
  headStyle,
  illustrationContainerStyle,
  svgStyle,
} from "./Illustration.css";
import { WhitelabelIllustration } from "./WhitelabelIllustration";

export type IllustrationProps = ClassNameProp &
  Pick<HTMLAttributes<HTMLElement>, "style"> &
  RecipeVariants<typeof illustrationContainerStyle> & {
    /**
     * The name of the illustration
     */
    name: IllustrationName;
    /**
     * The alt text for the illustration
     */
    alt?: string;
    /**
     * Enable rotation parallax effect on the illustration head
     */
    parallax?: boolean;
    /**
     * Parallax intensity (0-1), defaults to 0.01
     */
    parallaxIntensity?: number;
    /**
     * Maximum rotation angle in degrees, defaults to 5
     */
    parallaxMaxRotation?: number;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLElement>;
  };

/**
 * Default Illustration component that holds two SVGs with animation support.
 * Uses a semantic figure element and supports animating the foreground SVG using Framer Motion.
 */
const DefaultIllustration: FC<IllustrationProps> = ({
  name,
  size,
  className,
  alt,
  alignSelf,
  parallax = false,
  parallaxIntensity = 0.01,
  parallaxMaxRotation = 8,
  style,
  ref,
}) => {
  const { theme } = useTheme();
  const config = illustrations[name];
  const { parentHover } = useParentHover();

  // Add rotation parallax for the head
  const { ref: headParallaxRef, style: headParallaxStyle } =
    useRotationParallax<SVGSVGElement>({
      intensity: parallaxIntensity,
      enabled: parallax && !!config.head,
      baseRotation: config?.head?.rotation || 0,
      maxRotation: parallaxMaxRotation,
    });

  const src = useMemo(() => `/assets/images/illustrations/${name}.svg`, [name]);

  const [isLoaded, setIsLoaded] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const currentLoadedSrcRef = useRef<string | undefined>(undefined);

  // Handle SSR: Check if image is already loaded on mount
  useEffect(() => {
    const isDifferentSrc = currentLoadedSrcRef.current !== src;

    if (isDifferentSrc) {
      // Check if image is already complete (cached/SSR case)
      const imgElement = imageContainerRef.current?.querySelector("img");
      if (src && imgElement?.complete && imgElement.src.endsWith(src)) {
        // Image already loaded, set immediately without animation
        currentLoadedSrcRef.current = src;
        setIsLoaded(true);
      } else {
        // New image needs to load, reset loaded state
        setIsLoaded(false);
      }
    }
  }, [src]);

  const handleLoad = () => {
    currentLoadedSrcRef.current = src;
    setIsLoaded(true);
  };

  return (
    <figure
      ref={ref}
      className={mergeClasses(
        illustrationContainerStyle({
          size,
          alignSelf,
          loaded: isLoaded,
          hover: parentHover,
        }),
        className,
      )}
      role="img"
      aria-label={alt || name}
      {...(parentHover && { "data-hover": true })}
      style={style}
    >
      <Image
        ref={imageContainerRef}
        className={svgStyle({ theme: theme === "light" ? "light" : "dark" })}
        src={src}
        alt={alt || name}
        width="100%"
        height="100%"
        fit="contain"
        placeholder={false}
        onLoad={handleLoad}
      />
      {config?.head && (
        <IllustrationHead
          ref={headParallaxRef}
          face={config.head?.face}
          direction={config.head?.direction}
          className={headStyle()}
          style={{
            position: "absolute",
            left: config.head?.position.left
              ? `${config.head?.position.left}%`
              : undefined,
            top: config.head?.position.top
              ? `${config.head?.position.top}%`
              : undefined,
            width: config.head?.size ? `${config.head?.size}%` : undefined,
            height: config.head?.size ? `${config.head?.size}%` : undefined,
            ...headParallaxStyle,
            transform:
              !parallax && parentHover
                ? `rotate(${(config.head?.rotation || 0) - (config.head?.animations?.hoverDirection === "right" ? -4.5 : 4.5)}deg)`
                : headParallaxStyle.transform,
            zIndex: config.head?.index !== undefined ? config.head?.index : 1,
          }}
        />
      )}
    </figure>
  );
};

/**
 * Illustration component that holds two SVGs with animation support.
 * Returns theme-specific implementations based on the current theme.
 */
export const Illustration: FC<IllustrationProps> = ({ ref, ...props }) => {
  const { theme } = useTheme();

  // Use whitelabel illustration for all themes except light and dark
  if (theme !== "light" && theme !== "dark") {
    return <WhitelabelIllustration ref={ref} {...props} />;
  }

  return <DefaultIllustration ref={ref} {...props} />;
};
